/* @flow */
import clamp from "lodash/clamp";
import { getScaleX, getScaleY } from "./Scales";
import { getScaleModifier } from "./OscillatorVisuals";
import * as Helpers from "./NextGenChartMath";
import * as Types from "./NextGenChartTypes";
import throttle from "lodash/throttle"

type SetState = (
  (Types.State, Types.Props) => $Shape<Types.State> | null
) => void;

const SNAPSHOT_KEY = "chartEventHandlersLastState";

export const saveSnapshot = throttle((state) => {
  try {
    sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({
      cursor: state.cursor.follow ? undefined : {...state.cursor},
      timeSpan: state.timeSpan,
      interval: state.interval
    }));
  } catch (e) {
    console.error(e);
  };
}, 500);

function getSnapshot() {
  let lastState;
  try {
    const item = sessionStorage.getItem(SNAPSHOT_KEY);
    if (item) {
      lastState = JSON.parse(item);
    }
  } catch (e) {}

  return lastState;
}

export function clearSnapshot() {
  try {
    sessionStorage.removeItem(SNAPSHOT_KEY);
  } catch (e) {
    console.error(e);
  };
}

export function getInitialState(props: Types.Props): Types.State {
  // const present = props.points[props.points.length - 1];

  const interval = clamp(
    props.settings.interval,
    1,
    Helpers.isShortHistoryStock(props.stock)
      ? 1 * 60 * 2
      : 1 * 60 * 60 * 24 * 30
  );

  let snapshot = props.static ? {} : getSnapshot() || {};
  const state = {
    timeSpan: props.settings.timeSpan,
    animateTimeSpan: true,
    cursor:
      props.cursor != null
        ? props.cursor
        : {
            ratio: 0.68,
            date: props.points[props.points.length - 1].date,
            follow: true,
            animate: true
          },
    mouseX: 0,
    mouseY: 0,
    isHovered: false,
    isActive: false,
    width: 0,
    height: 0,
    pinchRatio: 0,
    extent: [0, 0],

    cache: {},
    lastLoadedSections: null,
    isLoading: false,
    isEngaged: false,
    isScrolled: false,
    lastEngagementTime: 0,
    lastScrollTime: 0,
    interval: interval,
    checkpoints: [],
    lastMovementTime: props.points[props.points.length - 1].date,
    ...snapshot
  };

  return {
    ...state,
    ...normalizeTimeSpan(state, props)
  };
}

export function makePayloadProps(
  state: Types.State,
  props: Types.Props
): Types.PayloadProps {
  return {
    stock: props.stock.stock,
    realtimeCache: props.points,
    width: state.width,
    height: state.height,
    timeSpan: state.timeSpan,
    sectionSize: props.settings.chartType === "line" ? 100 : 20,
    scaleX: getScaleX(state),
    type: props.settings.chartType,
    density: 1 / 5,
    interval: props.settings.interval,
    checkpoints: state.checkpoints
  };
}

export function updateIsLoading(
  state: Types.State,
  props: Types.Props
): $Shape<Types.State> {
  // В режиме OTC нет нужды следить за состоянием загрузки
  if (Helpers.isShortHistoryStock(props.stock)) {
    const isLoading = false;

    if (isLoading !== state.isLoading) {
      return { isLoading };
    } else {
      return null;
    }
  }

  const payloadProps = makePayloadProps(state, props);

  const relevantSectionProps = Helpers.getCommonSectionProps(payloadProps);
  let relevantSections = Helpers.generateSections(
    relevantSectionProps,
    payloadProps
  );

  let { isDarkArea } = Helpers.getPoints(
    relevantSections,
    state.cache,
    payloadProps,
    props.deals
  );

  let isLoading = false;

  if (isDarkArea) {
    relevantSections = Helpers.generateSections(
      { ...relevantSectionProps, interval: 5000 },
      payloadProps
    );
  }

  isLoading = relevantSections.some(
    section =>
      Helpers.getSectionBoundaries(section)[0] < props.points[0].date &&
      Helpers.getSectionType(section, payloadProps) === "past" &&
      state.cache[Helpers.getSectionKey(section)] == null
  );

  if (isLoading !== state.isLoading) {
    return { isLoading };
  } else {
    return null;
  }
}

export const updateExtent = function(
  state: Types.State,
  props: Types.Props
): $Shape<Types.State> {
  if (state.isLoading) {
    return null;
  } else {
    const payloadProps = makePayloadProps(state, props);

    const relevantSectionProps = Helpers.getCommonSectionProps(payloadProps);
    let relevantSections = Helpers.generateSections(
      relevantSectionProps,
      payloadProps
    );

    let { points, isDarkArea } = Helpers.getPoints(
      relevantSections,
      state.cache,
      payloadProps,
      props.deals
    );

    // Второй круг для тёмных областей графика
    if (isDarkArea) {
      relevantSections = Helpers.generateSections(
        { ...relevantSectionProps, interval: 5000 },
        payloadProps
      );

      const fallback = Helpers.getPoints(
        relevantSections,
        state.cache,
        payloadProps,
        props.deals
      );

      points = fallback.points;
      // emptySections = fallback.emptySections;
      isDarkArea = fallback.isDarkArea;
    }

    const scaleX = getScaleX(state);

    const oldestRelevantDate = scaleX.invert(0).valueOf();
    const newestRelevantDate = scaleX.invert(state.width).valueOf();

    const length = points.length;

    if (length > 1) {
      let minimum = null;
      let maximum = null;

      for (let i = 1; i < length; i++) {
        if (points[i].epoch * 1000 < oldestRelevantDate) {
          continue;
        }
        if (points[i].epoch * 1000 > newestRelevantDate) {
          break;
        }
        if (minimum == null || points[i].min < minimum) {
          minimum = points[i].min;
        }
        if (maximum == null || points[i].max > maximum) {
          maximum = points[i].max;
        }
      }

      if (minimum == null || maximum == null) {
        return null;
      } else {
        return { extent: [minimum, maximum] };
      }
    } else {
      return null;
    }
  }
};

export function normalizeTimeSpan(
  state: Types.State,
  props: Types.Props
): $Shape<Types.State> {
  const fittingTimeSpan =
    props.settings.chartType === "line"
      ? clamp(state.timeSpan, 30000, Helpers.getHistoryLength(props.stock))
      : clamp(
          state.timeSpan,
          ...Helpers.getTimeSpanRange(props.stock, state.interval * 1000)
        );

  if (fittingTimeSpan === state.timeSpan) {
    return null;
  } else {
    return {
      timeSpan: fittingTimeSpan
    };
  }
}

export function normalizeViewport(
  state: Types.State,
  props: Types.Props
): $Shape<Types.State> {
  // Так мы проверяем, можно ли двигать график
  const present = props.points[props.points.length - 1];

  const scaleX = getScaleX(state);

  const historyLenth = Helpers.getHistoryLength(props.stock);

  switch (true) {
    case scaleX(present.date - historyLenth) > 0:
      return {
        cursor: {
          date: state.cursor.date,
          ratio:
            (state.cursor.ratio * state.width -
              scaleX(present.date - historyLenth)) /
            state.width,
          follow: false,
          animate: false
        }
      };
    case scaleX(present.date) <= 100 && (state.isActive || state.isScrolled):
      return {
        cursor: {
          date: state.cursor.date,
          ratio:
            (state.cursor.ratio * state.width + 100 - scaleX(present.date)) /
            state.width,
          follow: false,
          animate: false
        }
      };
    case scaleX(present.date) < state.width &&
      !(state.isActive || state.isScrolled):
      return {
        cursor: {
          date: state.cursor.date,
          ratio: state.cursor.ratio,
          follow: true,
          animate: true
        }
      };
    default:
      return null;
  }
}

export function resetCursor(state: Types.State, props: Types.Props) {
  const present = props.points[props.points.length - 1];
  const scaleX = getScaleX(state);
  let presentX = scaleX(present.date);

  if (presentX >= 0 && presentX <= state.width) {
    return {
      cursor: {
        date: present.date,
        ratio: presentX / state.width,
        follow: true,
        animate: false
      }
    };
  } else {
    return null;
  }
}

export function onDrop(event: SyntheticWheelEvent<*>, setState: SetState) {
  event.preventDefault();
}

export const onWheel  = function(event: SyntheticWheelEvent<*>, setState: SetState) {
  event.preventDefault();
  const timeStamp = event.timeStamp;

  setState(() => ({
    isScrolled: true,
    lastScrollTime: timeStamp
  }));

  // Мы не хотим запутать клиента одновременным скроллом и зумом, поэтому
  // выбираем одно из двух.
  // Оси инвертируются с зажатой клавишей Shift.
  const [deltaX, deltaY] = event.shiftKey
    ? Math.abs(event.deltaY) > Math.abs(event.deltaX)
      ? [event.deltaY, 0]
      : [0, event.deltaX]
    : Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? [event.deltaX, 0]
      : [0, event.deltaY];

  const clientX = event.clientX;

  if (deltaX !== 0) {
    setState((state, props) => {
      return {
        cursor: {
          date: state.cursor.date,
          ratio: (state.cursor.ratio * state.width - deltaX) / state.width,
          follow: false,
          animate: false
        }
      };
    });
  }

  if (deltaY !== 0) {
    setState((state, props) => {
      const scaleX = getScaleX(state);

      return {
        cursor: {
          date: scaleX.invert(clientX).valueOf(),
          ratio: clientX / state.width,
          animate: false,
          follow: false
        },
        timeSpan:
          deltaY > 0
            ? state.timeSpan * (1 + Math.abs(deltaY) / 500)
            : state.timeSpan / (1 + Math.abs(deltaY) / 500),
        animateTimeSpan: true
      };
    });
    setState(normalizeTimeSpan);
  }

  setState(normalizeViewport);
  setState(updateIsLoading);
  setState(updateExtent);
  setState(saveSnapshot);
}

let isDragHandleDown = false;
let dragHandleHandler = null;

function graphicalModeClick(event, state, props) {
  const target = event.currentTarget || event.target;
  const rect = target.getBoundingClientRect();

  if (props.graphicalMode && props.graphicalMode.graphicalTool !== null) {
    const clientY = event.type === "touchstart" ? event.touches[0].clientY : event.clientY;
    const mouseY = clientY - rect.top;
    const scaleY = getScaleY({...state, modifier: getScaleModifier(props.settings.indicators) });

    const minLevel = scaleY.domain()[0];
    const value = scaleY.invert(mouseY).valueOf();
    const ticksHeight = 24;

    if (mouseY < ticksHeight || value < minLevel) {
      return props.dispatch({
        type: "SET_GRAPHICAL_MODE",
        payload: null
      });
    }

    props.dispatch({
      type: "PUSH_GRAPHICAL_TOOL",
      payload: {
        enabled: true,
        stock: props.stock.stock,
        tool: props.graphicalMode.graphicalTool,
        color: "#00cbfe",
        width: 2,
        anchorValue: value
      }
    });
  }
}

export const onMouseDown = function(event: SyntheticMouseEvent<*>, setState: SetState) {
  event.preventDefault();
  // Закомментировано пока мы не работаем в синтетической системе событий реакта
  // event.persist();

  if (event.target.isDragHandleDown) {
    isDragHandleDown = true;
    dragHandleHandler = event.target.dragHandleHandler;
  }

  const { clientX } = event;

  setState((state, props) => {
    graphicalModeClick(event, state, props);

    if (isDragHandleDown) {
      return state;
    }

    const scaleX = getScaleX(state);
    return {
      isActive: true,
      cursor: {
        date: Math.round(scaleX.invert(clientX).valueOf()),
        ratio: clientX / state.width,
        animate: false,
        follow: false
      }
    };
  });
};

export function onMouseUp(event: SyntheticEvent<*>, setState: SetState) {
  event.preventDefault();

  isDragHandleDown = false;
  dragHandleHandler = null;

  setState(() => ({ isActive: false }));

  setState(normalizeViewport);
  setState(updateIsLoading);
  setState(updateExtent);
  setState(saveSnapshot);
}

export function onMouseMove(event: SyntheticMouseEvent<*>, setState: SetState) {
  event.preventDefault();

  if (isDragHandleDown && typeof dragHandleHandler === "function") {
    dragHandleHandler(event);
    return;
  }

  const target = event.currentTarget || event.target;
  const rect = target.getBoundingClientRect();

  const { clientX, clientY } = event;

  const mouseX = clientX - rect.left;
  const mouseY = clientY - rect.top;

  setState((state, props) => {
    if (!state.isActive) {
      return {
        rectTop: rect.top,
        isHovered: true,
        mouseX,
        mouseY
      };
    }

    return {
      rectTop: rect.top,
      isHovered: true,
      mouseX: clientX,
      mouseY: clientY,
      cursor: {
        date: state.cursor.date,
        ratio: clientX / state.width,
        animate: false,
        follow: false
      }
    };
  });

  setState(
    (state, props) => (state.isActive ? normalizeViewport(state, props) : null)
  );
}

function onPanStart(touch: Touch, setState: SetState) {
  setState((state, props) => {
    const scaleX = getScaleX(state);

    return {
      isActive: true,
      cursor: {
        date: scaleX.invert(touch.clientX).valueOf(),
        ratio: touch.clientX / state.width,
        animate: false,
        follow: false
      }
    };
  });
}

export function onMouseEnter(_: *, setState: SetState) {
  setState(() => ({ isHovered: true }));
}

export function onMouseLeave(_: *, setState: SetState) {
  isDragHandleDown = false;
  dragHandleHandler = null;

  setState(() => ({ isHovered: false }));
}

function onPanMove(touch: Touch, setState: SetState) {
  setState(state => {
    if (!state.isActive) {
      return {
        mouseX: touch.clientX,
        mouseY: touch.clientY
      };
    }

    return {
      mouseX: touch.clientX,
      mouseY: touch.clientY,
      cursor: {
        date: state.cursor.date,
        ratio: touch.clientX / state.width,
        animate: false,
        follow: false
      }
    };
  });

  setState(
    (state, props) => (state.isActive ? normalizeViewport(state, props) : null)
  );
}

function onPinchStart(
  [touchOne, touchTwo]: [Touch, Touch],
  setState: SetState
) {
  setState((state, props) => {
    const clientX = (touchOne.clientX + touchTwo.clientX) / 2;
    const pinch = Math.abs(touchOne.clientX - touchTwo.clientX);

    const scaleX = getScaleX(state);

    return {
      isActive: true,
      pinchRatio: state.timeSpan * pinch,
      cursor: {
        date: scaleX.invert(clientX).valueOf(),
        ratio: clientX / state.width,
        animate: false,
        follow: false
      },
      animateTimeSpan: false
    };
  });
}

function onPinchMove([touchOne, touchTwo]: [Touch, Touch], setState: SetState) {
  const clientX = (touchOne.clientX + touchTwo.clientX) / 2;
  const pinch = Math.abs(touchOne.clientX - touchTwo.clientX);

  setState((state, props) => {
    return {
      isActive: true,
      cursor: {
        date: state.cursor.date,
        ratio: clientX / state.width,
        animate: false,
        follow: false
      },
      timeSpan: state.pinchRatio / pinch,
      animateTimeSpan: false
    };
  });

  setState(normalizeTimeSpan);

  setState(
    (state, props) => (state.isActive ? normalizeViewport(state, props) : null)
  );
}

export const onTouchStart = function(
  event: SyntheticTouchEvent<*>,
  setState: SetState
) {

  if (event.target.refundClick) {
    return false;
  }

  event.preventDefault();
  // Закомментировано пока мы не работаем в синтетической системе событий реакта
  // event.persist();

  if (event.target.isDragHandleDown) {
    isDragHandleDown = true;
    dragHandleHandler = event.target.dragHandleHandler;
  }

  setState((state, props) => {
    graphicalModeClick(event, state, props);

    return { isEngaged: true, lastEngagementTime: Date.now() };
  });

  switch (event.touches.length) {
    case 1:
      return onPanStart(event.touches.item(0), setState);
    case 2:
      return onPinchStart(
        [event.touches.item(0), event.touches.item(1)],
        setState
      );
    default:
      return;
  }
};

export function onTouchEnd(event: SyntheticTouchEvent<*>, setState: SetState) {
  return onMouseUp(event, setState);
}

export const onTouchMove = function(event: SyntheticTouchEvent<*>, setState: SetState) {
  event.preventDefault();

  if (isDragHandleDown && typeof dragHandleHandler === "function") {
    dragHandleHandler(event);
    return;
  }

  switch (event.touches.length) {
    case 1:
      return onPanMove(event.touches.item(0), setState);
    case 2:
      return onPinchMove(
        [event.touches.item(0), event.touches.item(1)],
        setState
      );
    default:
      return;
  }
};



// WEBPACK FOOTER //
// ./src/components/Chart/ChartEventHandlers.js